<template>
  <CCard class="mb-3">
    <CCardHeader class="section-header">
      <fa-icon icon="user-tie" class="mr-2" />
      <strong>Representantes e Metas</strong>
      <small class="validation-error ml-2">{{ erro }}</small>
    </CCardHeader>
    <CCardBody>
      <!-- Lista de Representantes -->
      <div v-if="metas && metas.length > 0" class="mb-3">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th width="80">Código</th>
                <th>Nome</th>
                <th width="120">Positivação</th>
                <th width="140">% Mix</th>
                <th width="140">Valor Meta</th>
                <th width="80" class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(meta, index) in metas" :key="meta.codRep">
                <td>
                  <CBadge color="primary">{{ meta.codRep }}</CBadge>
                </td>
                <td>
                  <fa-icon icon="user-tie" class="text-muted mr-2" />
                  {{ getNomeRepresentante(meta) }}
                </td>
                <td>{{ toPercent(meta.positivacao) }}</td>
                <td>{{ toPercent(meta.percMix) }}</td>
                <td>
                  <strong class="text-success">{{ toMonetary(meta.vlMeta) }}</strong>
                </td>
                <td class="text-center">
                  <CButton
                    color="danger"
                    size="sm"
                    @click="$emit('remover', index)"
                    v-c-tooltip="'Remover representante'"
                  >
                    <fa-icon icon="trash-alt" />
                  </CButton>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="table-total">
                <td colspan="4" class="text-right font-weight-bold">TOTAL:</td>
                <td colspan="2" class="font-weight-bold text-success">
                  {{ toMonetary(totalMetas) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-small">
        <fa-icon icon="user-tie" size="2x" class="text-muted mb-2" />
        <p class="text-muted mb-0">Nenhum representante adicionado</p>
        <small class="text-muted">Adicione representantes abaixo para definir as metas dos vendedores externos</small>
      </div>

      <hr class="my-3" />

      <div class="d-flex align-items-center justify-content-between mb-3">
        <h2 class="mb-0">ADICIONAR REPRESENTANTE:</h2>
        <div class="custom-control custom-checkbox">
          <input
            type="checkbox"
            class="custom-control-input"
            id="modoLoteRepresentantes"
            v-model="modoLote"
          />
          <label class="custom-control-label" for="modoLoteRepresentantes">
            <fa-icon icon="users" class="mr-1" />
            Adicionar em Lote
          </label>
        </div>
      </div>

      <!-- Valores da meta: individual e lote usam os mesmos campos -->
      <div class="xrow">
        <div class="col-1-4" v-if="!modoLote">
          <div class="form-group">
            <label>Representante</label>
            <select class="form-control" v-model="codRepSelecionado">
              <option value>Selecione</option>
              <option
                v-for="representante in representantesDisponiveis"
                :key="representante.codRep"
                :value="representante.codRep"
              >
                {{ representante.codRep }} - {{ representante.nome }}
              </option>
            </select>
          </div>
        </div>
        <div :class="modoLote ? 'col-1-3' : 'col-1-4'">
          <div class="form-group">
            <label>Positivação</label>
            <the-mask
              :mask="['#%', '##%']"
              class="form-control"
              type="text"
              v-model="meta.positivacao"
            />
          </div>
        </div>
        <div :class="modoLote ? 'col-1-3' : 'col-1-4'">
          <div class="form-group">
            <label>Percentual de Mix</label>
            <the-mask
              :mask="['#%', '##%']"
              class="form-control"
              type="text"
              v-model="meta.percMix"
            />
          </div>
        </div>
        <div :class="modoLote ? 'col-1-3' : 'col-1-4'">
          <div class="form-group">
            <label>Valor Meta</label>
            <money v-if="modoLote" class="form-control" v-model="meta.vlMeta" v-bind="monetary" />
            <div v-else class="input-group">
              <money class="form-control" v-model="meta.vlMeta" v-bind="monetary" />
              <button
                class="button button-primary input-group-addon-right"
                :disabled="!podeAdicionarIndividual"
                title="Adicionar"
                v-on:click="adicionarIndividual"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Seleção em lote -->
      <div v-if="modoLote" class="representantes-selection">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <label class="font-weight-bold mb-0">
            <fa-icon icon="users" class="mr-1" />
            Selecionar Representantes ({{ selecionados.length }} selecionados)
          </label>
          <CButton
            v-if="representantesPorBusca.length > 0"
            color="secondary"
            size="sm"
            @click="alternarTodos"
          >
            <fa-icon :icon="todosSelecionados ? 'check-square' : 'square'" class="mr-1" />
            {{ todosSelecionados ? 'Desmarcar' : 'Marcar' }} Todos
          </CButton>
        </div>

        <div class="form-group mb-2">
          <input
            type="text"
            class="form-control form-control-sm"
            v-model="filtro"
            placeholder="Buscar por nome ou código..."
          />
        </div>

        <div class="representantes-grid">
          <label
            v-for="representante in representantesPorBusca"
            :key="representante.codRep"
            class="representante-item"
          >
            <input type="checkbox" :value="representante.codRep" v-model="selecionados" />
            <span class="representante-codigo">{{ representante.codRep }}</span>
            <span class="representante-nome">{{ representante.nome }}</span>
          </label>
        </div>
        <div v-if="!possuiRepresentantes" class="text-center text-muted py-3">
          Nenhum representante disponível
        </div>
        <div v-else-if="representantesDisponiveis.length === 0" class="text-center text-muted py-3">
          Todos os representantes já foram adicionados
        </div>
        <div v-else-if="representantesPorBusca.length === 0" class="text-center text-muted py-3">
          <fa-icon icon="search" class="mr-1" />
          Nenhum representante encontrado com "{{ filtro }}"
        </div>

        <div class="text-right mt-3">
          <CButton
            color="secondary"
            @click="limparSelecao"
            :disabled="selecionados.length === 0"
            class="mr-2"
          >
            <fa-icon icon="times" class="mr-1" />
            Limpar Seleção
          </CButton>
          <CButton color="primary" @click="adicionarLote" :disabled="!podeAdicionarLote">
            <fa-icon icon="plus-circle" class="mr-1" />
            Adicionar {{ selecionados.length }} Representante(s)
          </CButton>
        </div>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { toMonetary, toPercent } from "@/helpers/utils";
import { metaPreenchida, mesmoCodigoRepresentante, somaMetas } from "@/helpers/campanhas";

function metaPadrao() {
  return {
    vlMeta: "1",
    positivacao: "1",
    percMix: "1",
  };
}

export default {
  name: "MetasRepresentantes",
  props: {
    metas: Array,
    representantes: Array,
    erro: String,
  },
  data() {
    return {
      modoLote: false,
      codRepSelecionado: "",
      meta: metaPadrao(),
      selecionados: [],
      filtro: "",
      monetary: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        masked: false,
      },
    };
  },
  computed: {
    totalMetas() {
      return somaMetas(this.metas);
    },
    representantesDisponiveis() {
      const metas = this.metas || [];
      return (this.representantes || []).filter(
        (representante) =>
          !metas.some((meta) => mesmoCodigoRepresentante(meta.codRep, representante.codRep))
      );
    },
    representantesPorBusca() {
      if (!this.filtro) {
        return this.representantesDisponiveis;
      }
      const busca = this.filtro.toLowerCase();
      return this.representantesDisponiveis.filter((representante) => {
        const nome = (representante.nome || "").toLowerCase();
        const codigo = String(representante.codRep).toLowerCase();
        return nome.includes(busca) || codigo.includes(busca);
      });
    },
    todosSelecionados() {
      return (
        this.representantesPorBusca.length > 0 &&
        this.representantesPorBusca.every((representante) =>
          this.selecionados.includes(representante.codRep)
        )
      );
    },
    possuiRepresentantes() {
      return (this.representantes || []).length > 0;
    },
    metaValida() {
      return metaPreenchida(this.meta);
    },
    podeAdicionarIndividual() {
      return !!this.codRepSelecionado && this.metaValida;
    },
    podeAdicionarLote() {
      return this.selecionados.length > 0 && this.metaValida;
    },
  },
  methods: {
    novaMeta(codRep) {
      return {
        codRep,
        vlMeta: this.meta.vlMeta,
        positivacao: this.meta.positivacao,
        percMix: this.meta.percMix,
      };
    },
    adicionarIndividual() {
      if (!this.podeAdicionarIndividual) return;
      this.$emit("adicionar", [this.novaMeta(this.codRepSelecionado)]);
      this.codRepSelecionado = "";
      this.meta = metaPadrao();
    },
    adicionarLote() {
      if (!this.podeAdicionarLote) return;
      const metas = this.selecionados.map((codRep) => this.novaMeta(codRep));
      this.$emit("adicionar", metas);
      this.limparSelecao();
      this.$swal({
        title: "Sucesso!",
        text: `${metas.length} representante(s) adicionado(s)`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    },
    alternarTodos() {
      const codigos = this.representantesPorBusca.map((representante) => representante.codRep);
      if (this.todosSelecionados) {
        this.selecionados = this.selecionados.filter((codRep) => !codigos.includes(codRep));
      } else {
        this.selecionados = [...new Set([...this.selecionados, ...codigos])];
      }
    },
    limparSelecao() {
      this.selecionados = [];
      this.filtro = "";
    },
    getNomeRepresentante(meta) {
      const representante = (this.representantes || []).find((rep) =>
        mesmoCodigoRepresentante(rep.codRep, meta.codRep)
      );
      return (representante && representante.nome) || meta.nome || "—";
    },
    toMonetary,
    toPercent,
  },
};
</script>

<style scoped>
.representantes-selection {
  border: 1px solid #e4e7ea;
  border-radius: 0.25rem;
  padding: 1rem;
}

.representantes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.5rem;
  max-height: 260px;
  overflow-y: auto;
}

.representante-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.375rem 0.5rem;
  border: 1px solid #e4e7ea;
  border-radius: 0.25rem;
  cursor: pointer;
}

.representante-codigo {
  font-weight: 600;
  color: #3a3e81;
}

.representante-nome {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
